import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { LocationSelect } from "../components/LocationSelect";
import { Send, CheckCircle2 } from "lucide-react";

export default function PostRequest() {
  const { t, lang } = useLang();
  const [meta, setMeta] = useState({ sectors: [], locations: [] });
  const [form, setForm] = useState({
    title: "", sector: "", location: "", budget: "", description: "",
    urgency: "normal", contact_name: "", contact_email: "", contact_phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.sector || !form.location) { toast.error("Secteur et localisation requis"); return; }
    setBusy(true);
    try {
      await api.post("/requests", form);
      toast.success(lang === "fr" ? "Demande publiée !" : "Request posted!");
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-[calc(100vh-4rem)] grid place-items-center px-4">
        <div className="text-center max-w-md" data-testid="post-success">
          <div className="grid place-items-center h-16 w-16 rounded-2xl bg-emerald-500/10 text-emerald-600 mx-auto mb-6">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h1 className="font-display text-3xl font-extrabold mb-3">{lang === "fr" ? "Demande publiée !" : "Request posted!"}</h1>
          <p className="text-muted-foreground mb-8">{lang === "fr" ? "Un travailleur autonome vous contactera bientôt." : "A freelancer will contact you soon."}</p>
          <Button data-testid="post-another-btn" onClick={() => { setDone(false); setForm({ title: "", sector: "", location: "", budget: "", description: "", urgency: "normal", contact_name: "", contact_email: "", contact_phone: "" }); }}>
            {lang === "fr" ? "Publier une autre demande" : "Post another request"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">{t("post_title")}</h1>
      <p className="text-muted-foreground mb-8">{t("post_sub")}</p>
      <form onSubmit={submit} className="space-y-5 rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm">
        <div>
          <Label htmlFor="title">{t("f_title")}</Label>
          <Input id="title" required data-testid="post-title-input" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" />
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>{t("f_sector")}</Label>
            <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
              <SelectTrigger className="mt-1.5" data-testid="post-sector-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {meta.sectors.map((s) => <SelectItem key={s.id} value={s.id}>{lang === "fr" ? s.fr : s.en}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("f_location")}</Label>
            <LocationSelect groups={meta.location_groups || []} value={form.location}
              onChange={(v) => setForm({ ...form, location: v })} testId="post-location-select"
              className="mt-1.5" placeholder="—" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="budget">{t("f_budget")}</Label>
            <Input id="budget" data-testid="post-budget-input" value={form.budget}
              onChange={(e) => setForm({ ...form, budget: e.target.value })} className="mt-1.5" placeholder="500-1000$" />
          </div>
          <div>
            <Label>{t("f_urgency")}</Label>
            <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
              <SelectTrigger className="mt-1.5" data-testid="post-urgency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">{t("urgency_normal")}</SelectItem>
                <SelectItem value="urgent">{t("urgency_urgent")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label htmlFor="desc">{t("f_desc")}</Label>
          <Textarea id="desc" required rows={4} data-testid="post-description-input" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
        </div>
        <div className="border-t border-border pt-5 grid sm:grid-cols-3 gap-5">
          <div>
            <Label htmlFor="cname">{t("f_name")}</Label>
            <Input id="cname" required data-testid="post-name-input" value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="cemail">{t("f_email")}</Label>
            <Input id="cemail" type="email" required data-testid="post-email-input" value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="cphone">{t("f_phone")}</Label>
            <Input id="cphone" data-testid="post-phone-input" value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="mt-1.5" />
          </div>
        </div>
        <Button type="submit" size="lg" className="w-full" disabled={busy} data-testid="post-submit-btn">
          <Send className="h-4 w-4 mr-2" /> {busy ? t("processing") : t("f_submit")}
        </Button>
      </form>
    </div>
  );
}
